/**
 * Carousel Component
 * 
 * An advanced image carousel with WebGL-powered transitions using Three.js.
 * Features smooth shader-based image transitions, keyboard/touch navigation,
 * and accessibility support.
 * 
 * Key Features:
 * - WebGL displacement transitions between images
 * - Keyboard navigation (arrow keys)
 * - Touch/swipe support
 * - Automatic image preloading
 * - Reduced motion support
 * - Screen reader accessibility
 * - Progressive image loading with placeholders
 */

import { useState, useEffect, useRef, useCallback } from 'react';
// Three.js imports for WebGL rendering
import {
  WebGLRenderer,        // Core WebGL renderer
  LinearFilter,         // Texture filtering for smooth images
  OrthographicCamera,   // 2D camera for UI rendering
  Scene,               // 3D scene container
  PlaneBufferGeometry, // Geometry for the image plane
  TextureLoader,       // Loads images as WebGL textures
  ShaderMaterial,      // Custom shader material for transitions
  Mesh,               // 3D object (geometry + material)
  Color,              // Color utilities
  sRGBEncoding,       // Color space encoding
} from 'three';
import { spring, value, listen, pointer } from 'popmotion'; // Animation and interaction utilities
import classNames from 'classnames'; // CSS class utility
import { usePrefersReducedMotion, useInViewport } from 'hooks'; // Custom hooks
import prerender from 'utils/prerender'; // Server-side rendering utilities
import { blurOnMouseUp } from 'utils/focus'; // Focus management
import { ReactComponent as ArrowLeft } from 'assets/arrow-left.svg';
import { ReactComponent as ArrowRight } from 'assets/arrow-right.svg';
import { vertex, fragment } from './carouselShader';
import { cleanScene, cleanRenderer } from 'utils/three';
import { getImageFromSrcSet } from 'utils/image'; // Utility for responsive image selection
import './index.css';

/**
 * Determines the next image index based on direction and current state
 * @param {number} imageIndex - Current image index
 * @param {number|null} index - Explicit index (overrides direction)
 * @param {Array} images - Array of image objects
 * @param {number} direction - Direction of navigation (1 for next, -1 for previous)
 * @returns {number} The calculated next index
 */
function determineIndex(imageIndex, index, images, direction) {
  if (index !== null) return index;
  const length = images.length;
  // Calculate previous and next indices with wraparound
  const prevIndex = (imageIndex - 1 + length) % length;
  const nextIndex = (imageIndex + 1) % length;
  const finalIndex = direction > 0 ? nextIndex : prevIndex;
  return finalIndex;
}

/**
 * Main Carousel Component
 * 
 * Renders an interactive image carousel with WebGL-powered transitions.
 * Uses Three.js for hardware-accelerated rendering and custom shaders
 * for smooth displacement effects between images.
 */
const Carousel = ({ width, height, images, placeholder, ...rest }) => {
  // State management
  const [dragging, setDragging] = useState(false);           // Track if user is dragging/swiping
  const [imageIndex, setImageIndex] = useState(0);           // Current active image index
  const [loaded, setLoaded] = useState(false);               // Whether carousel has loaded
  const [showPlaceholder, setShowPlaceholder] = useState(true); // Show placeholder while loading
  const [textures, setTextures] = useState();               // WebGL textures for images
  const [canvasRect, setCanvasRect] = useState();           // Canvas bounding rectangle for interactions
  
  // Refs for Three.js objects and DOM elements
  const canvas = useRef();          // Canvas DOM element
  const imagePlane = useRef();      // Three.js mesh for displaying images
  const geometry = useRef();        // Plane geometry
  const material = useRef();        // Shader material for transitions
  const scene = useRef();           // Three.js scene
  const camera = useRef();          // Orthographic camera
  const renderer = useRef();        // WebGL renderer
  
  // Animation and interaction state
  const animating = useRef(false);              // Prevent multiple simultaneous animations
  const swipeDirection = useRef();              // Direction of current swipe gesture
  const lastSwipePosition = useRef();           // Last pointer position for swipe calculation
  const scheduledAnimationFrame = useRef();     // For cleanup of animation frames
  
  // Hooks for accessibility and performance
  const prefersReducedMotion = usePrefersReducedMotion(); // Respect user's motion preferences
  const inViewport = useInViewport(canvas, true);         // Only animate when visible
  
  // Additional refs for animation and interaction
  const placeholderRef = useRef();   // Placeholder image reference
  const springTween = useRef();      // Spring animation controller
  const swipeElement = useRef();     // Element for swipe gesture detection

  // Generate accessible description for screen readers
  const currentImageAlt = `Slide ${imageIndex + 1} of ${images.length}. ${
    images[imageIndex].alt
  }`;

  // Update document cursor style during drag operations
  useEffect(() => {
    if (dragging) {
      document.body.style.cursor = 'grabbing'; // Show grabbing cursor while dragging
    }

    return () => {
      document.body.style.cursor = ''; // Reset cursor when component unmounts or drag ends
    };
  }, [dragging]);

  /**
   * Initialize Three.js scene, camera, and renderer
   * Sets up the WebGL context and basic scene configuration
   */
  useEffect(() => {
    // Configure orthographic camera for 2D rendering
    // [left, right, top, bottom, near, far]
    const cameraOptions = [width / -2, width / 2, height / 2, height / -2, 1, 1000];
    
    // Create WebGL renderer with performance optimizations
    renderer.current = new WebGLRenderer({
      canvas: canvas.current,
      antialias: false,                    // Disable antialiasing for performance
      powerPreference: 'high-performance', // Request discrete GPU if available
    });
    
    // Set up camera and scene
    camera.current = new OrthographicCamera(...cameraOptions);
    scene.current = new Scene();
    
    // Configure renderer settings
    renderer.current.setPixelRatio(2);          // High DPI display support
    renderer.current.setClearColor(0x111111, 1.0); // Dark gray background
    renderer.current.setSize(width, height);     // Set canvas dimensions
    
    // Style the canvas element for responsive behavior
    renderer.current.domElement.style.maxWidth = '100%';
    renderer.current.domElement.style.height = 'auto';
    
    // Set scene background and camera position
    scene.current.background = new Color(0x111111);
    camera.current.position.z = 1; // Position camera to view the image plane

    // Cleanup function to prevent memory leaks
    return () => {
      animating.current = false;
      cleanScene(scene.current);   // Clean up Three.js objects
      cleanRenderer(renderer.current); // Dispose of WebGL context
    };
  }, [height, width]); // Re-run when dimensions change

  useEffect(() => {
    let mounted = true; // Track component mount state to prevent memory leaks

    /**
     * Asynchronously load all carousel images as WebGL textures
     * Configures texture settings for optimal quality and performance
     */
    const loadImages = async () => {
      const textureLoader = new TextureLoader();
      const anisotropy = renderer.current.capabilities.getMaxAnisotropy(); // Get max texture quality

      // Load all images in parallel for better performance
      const texturePromises = images.map(async image => {
        // Get the appropriate image source for the current device/resolution
        const imageSrc = await getImageFromSrcSet(image);
        
        // Load the image as a WebGL texture
        const imageTexture = await textureLoader.loadAsync(imageSrc);
        
        // Initialize the texture on the GPU
        await renderer.current.initTexture(imageTexture);
        
        // Configure texture settings for quality
        imageTexture.encoding = sRGBEncoding;           // Correct color space
        imageTexture.minFilter = LinearFilter;          // Smooth scaling down
        imageTexture.magFilter = LinearFilter;          // Smooth scaling up
        imageTexture.anisotropy = anisotropy;           // Best quality filtering
        imageTexture.generateMipmaps = false;           // Disable mipmaps for UI textures
        
        return imageTexture;
      });

      // Wait for all textures to load
      const textures = await Promise.all(texturePromises);

      // Cancel if the component has unmounted during async code
      if (!mounted) return;

      // Create shader material with custom transition shaders
      material.current = new ShaderMaterial({
        uniforms: {
          dispFactor: { type: 'f', value: 0 },          // Transition progress (0-1)
          direction: { type: 'f', value: 1 },           // Transition direction (1 or -1)
          currentImage: { type: 't', value: textures[0] }, // Currently displayed image
          nextImage: { type: 't', value: textures[1] },    // Image transitioning to
        },
        vertexShader: vertex,        // Simple pass-through vertex shader
        fragmentShader: fragment,    // Custom displacement transition shader
        transparent: false,          // Opaque rendering for performance
        opacity: 1,
      });

      // Create plane geometry to display the images
      geometry.current = new PlaneBufferGeometry(width, height, 1);
      imagePlane.current = new Mesh(geometry.current, material.current);
      imagePlane.current.position.set(0, 0, 0); // Center the plane
      scene.current.add(imagePlane.current);     // Add to scene

      setLoaded(true);
      setTextures(textures);

      // Render the initial frame
      requestAnimationFrame(() => {
        renderer.current.render(scene.current, camera.current);
      });
    };

    // Only load images when the carousel enters the viewport to improve performance
    if (inViewport && !loaded) {
      loadImages();
    }

    return () => {
      mounted = false; // Prevent state updates after unmount
    };
  }, [height, images, inViewport, loaded, width]);

  /**
   * Navigate to a specific image index with animated transition
   * @param {Object} params - Navigation parameters
   * @param {number} params.index - Target image index
   * @param {number} params.direction - Transition direction (1 or -1)
   */
  const goToIndex = useCallback(
    ({ index, direction = 1 }) => {
      if (!textures) return; // Exit if textures haven't loaded yet
      
      setImageIndex(index); // Update current image index
      
      // Update shader uniforms for the transition
      const uniforms = material.current.uniforms;
      uniforms.nextImage.value = textures[index]; // Set the target image
      uniforms.direction.value = direction;       // Set transition direction

      // Callback when transition completes
      const onComplete = () => {
        uniforms.currentImage.value = textures[index]; // Update current image
        uniforms.dispFactor.value = 0;                 // Reset transition progress
        animating.current = false;                     // Allow new animations
      };

      // Animate transition if motion is not disabled
      if (!prefersReducedMotion && uniforms.dispFactor.value !== 1) {
        animating.current = true; // Block new animations during transition

        // Create a reactive value that updates the shader uniform
        const springValue = value(uniforms.dispFactor.value, value => {
          uniforms.dispFactor.value = value; // Update shader transition progress
          if (value === 1) onComplete();     // Complete when fully transitioned
        });

        // Create spring animation for smooth, natural feeling transitions
        springTween.current = spring({
          from: springValue.get(),            // Start from current progress
          to: 1,                             // Animate to full transition
          velocity: springValue.getVelocity(), // Maintain current velocity
          stiffness: 100,                    // Spring tension (speed)
          damping: 20,                       // Spring friction (smoothness)
        }).start(springValue);
      } else {
        // Skip animation if reduced motion is preferred
        onComplete();
        requestAnimationFrame(() => {
          renderer.current.render(scene.current, camera.current);
        });
      }
    },
    [prefersReducedMotion, textures]
  );

  /**
   * Navigate to the next/previous image with direction handling
   * Includes animation queuing to prevent conflicts
   */
  const navigate = useCallback(
    ({ direction, index = null, ...rest }) => {
      if (!loaded) return; // Wait for carousel to be ready

      // Queue navigation if already animating to prevent conflicts
      if (animating.current) {
        cancelAnimationFrame(scheduledAnimationFrame.current);
        scheduledAnimationFrame.current = requestAnimationFrame(() =>
          navigate({ direction, index, ...rest })
        );
        return;
      }

      // Calculate the target index
      const finalIndex = determineIndex(imageIndex, index, textures, direction);
      goToIndex({ index: finalIndex, direction: direction, ...rest });
    },
    [goToIndex, imageIndex, loaded, textures]
  );

  /**
   * Handle navigation button clicks with automatic direction calculation
   */
  const onNavClick = useCallback(
    index => {
      if (index === imageIndex) return; // Don't navigate to current image
      
      // Calculate direction based on target vs current index
      const direction = index > imageIndex ? 1 : -1;
      navigate({ direction, index });
    },
    [imageIndex, navigate]
  );

  /**
   * Track canvas position for accurate touch/mouse interaction
   */
  useEffect(() => {
    const handleResize = () => {
      const rect = canvas.current.getBoundingClientRect();
      setCanvasRect(rect); // Update canvas position for interaction calculations
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial position

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * Main animation loop - renders the scene when transitions are active
   */
  useEffect(() => {
    let animation;

    const animate = () => {
      animation = requestAnimationFrame(animate);
      if (animating.current) {
        renderer.current.render(scene.current, camera.current); // Render frame during transitions
      }
    };

    animation = requestAnimationFrame(animate); // Start the animation loop

    return () => {
      cancelAnimationFrame(animation); // Clean up animation loop
      springTween.current?.stop();    // Stop any running spring animations
    };
  }, []);

  /**
   * Handle placeholder image cleanup after transition completes
   */
  useEffect(() => {
    if (placeholder) {
      const purgePlaceholder = () => {
        setShowPlaceholder(false); // Remove placeholder after fade out
      };

      const placeholderElement = placeholderRef.current;
      placeholderElement.addEventListener('transitionend', purgePlaceholder);

      return () => {
        if (placeholderElement) {
          placeholderElement.removeEventListener('transitionend', purgePlaceholder);
        }
      };
    }
  }, [placeholder]);

  /**
   * Handle swipe/drag movement with real-time shader updates
   * Provides immediate visual feedback during user interaction
   */
  const onSwipeMove = useCallback(
    position => {
      if (animating.current || !material.current || !textures) return;
      
      const { x } = position; // Horizontal position of pointer/touch
      lastSwipePosition.current = x;
      const absoluteX = Math.abs(x);
      const containerWidth = canvasRect.width;
      
      // Determine swipe direction: left swipe = next image, right swipe = previous
      swipeDirection.current = x > 0 ? -1 : 1;
      
      // Calculate swipe progress as percentage (0-1)
      const swipePercentage = 1 - ((absoluteX - containerWidth) / containerWidth) * -1;
      
      // Determine which image should be the "next" image based on swipe direction
      const nextIndex = determineIndex(imageIndex, null, images, swipeDirection.current);
      
      // Update shader uniforms for real-time preview
      const uniforms = material.current.uniforms;
      const displacementClamp = Math.min(Math.max(swipePercentage, 0), 1); // Clamp to 0-1

      uniforms.currentImage.value = textures[imageIndex]; // Current image
      uniforms.nextImage.value = textures[nextIndex];     // Preview image
      uniforms.direction.value = swipeDirection.current;  // Transition direction

      // Update transition progress if motion is enabled
      if (!prefersReducedMotion) {
        uniforms.dispFactor.value = displacementClamp; // Real-time transition preview
      }

      // Render the frame to show the preview
      requestAnimationFrame(() => {
        renderer.current.render(scene.current, camera.current);
      });
    },
    [canvasRect, imageIndex, images, prefersReducedMotion, textures]
  );

  /**
   * Handle swipe/drag end - decide whether to complete or cancel the transition
   * Uses swipe distance and velocity to determine user intent
   */
  const onSwipeEnd = useCallback(() => {
    if (!material.current) return;
    
    const uniforms = material.current.uniforms;
    const duration = (1 - uniforms.dispFactor.value) * 1000; // Remaining animation time
    const position = Math.abs(lastSwipePosition.current);     // Final swipe distance
    const minSwipeDistance = canvasRect.width * 0.2;          // 20% of width threshold
    lastSwipePosition.current = 0; // Reset position tracker

    if (animating.current) return; // Don't interfere with existing animations
    if (position === 0 || !position) return; // No meaningful swipe detected

    // Complete the transition if swipe distance exceeds threshold
    if (position > minSwipeDistance) {
      navigate({
        duration,
        direction: swipeDirection.current,
      });
    } else {
      // Cancel the transition - swap images back and reverse the progress
      uniforms.currentImage.value = uniforms.nextImage.value;
      uniforms.nextImage.value = uniforms.currentImage.value;
      uniforms.dispFactor.value = 1 - uniforms.dispFactor.value;

      // Animate back to original state
      navigate({
        direction: swipeDirection.current * -1, // Reverse direction
        index: imageIndex,                      // Stay on current image
      });
    }
  }, [canvasRect, imageIndex, navigate]);

  /**
   * Set up swipe/drag interaction system using Popmotion
   * Handles both mouse and touch events for cross-platform compatibility
   */
  useEffect(() => {
    let pointerTracker;

    // Create a reactive value for tracking pointer movement
    const swipeXY = value({ x: 0, y: 0 }, position => {
      onSwipeMove(position); // Update carousel state on movement
    });

    // Listen for interaction start (mouse down or touch start)
    const swipeStart = listen(swipeElement.current, 'mousedown touchstart').start(
      event => {
        setDragging(true);    // Update UI state
        event.preventDefault(); // Prevent default browser behavior
        // Start tracking pointer movement
        pointerTracker = pointer({ x: 0, y: 0 }).start(swipeXY);
      }
    );

    // Listen for interaction end (mouse up or touch end)
    const swipeEnd = listen(document, 'mouseup touchend').start(() => {
      setDragging(false); // Update UI state
      onSwipeEnd();       // Process the swipe result
      pointerTracker?.stop(); // Stop any active pointer tracking
    });

    return () => {
      swipeStart.stop();  // Clean up event listeners
      swipeEnd.stop();
      pointerTracker?.stop();
    };
  }, [onSwipeEnd, onSwipeMove]);

  /**
   * Handle keyboard navigation for accessibility
   * Allows users to navigate with arrow keys
   */
  const handleKeyDown = event => {
    const actions = {
      ArrowRight: () => navigate({ direction: 1 }),  // Next image
      ArrowLeft: () => navigate({ direction: -1 }),  // Previous image
    };

    const selectedAction = actions[event.key];

    if (!!selectedAction) {
      selectedAction(); // Execute the navigation action
    }
  };

  /**
   * Render the carousel with accessibility features and interaction elements
   * Includes canvas for WebGL rendering, placeholder handling, and navigation controls
   */
  return (
    <div className="carousel" onKeyDown={handleKeyDown} {...rest}>
      <div className="carousel__content">
        {/* Main image display area with swipe detection */}
        <div
          className={classNames('carousel__image-wrapper', {
            'carousel__image-wrapper--dragging': dragging, // Visual feedback during drag
          })}
          ref={swipeElement} // Element for swipe gesture detection
        >
          {/* Canvas wrapper with accessibility attributes */}
          <div
            aria-atomic           // Screen reader reads entire content when it changes
            className="carousel__canvas-wrapper"
            aria-live="polite"    // Announce changes to screen readers
            aria-label={currentImageAlt} // Descriptive label for current image
            role="img"            // Identify as an image for accessibility
          >
            {/* WebGL canvas for rendering (hidden from screen readers) */}
            <canvas aria-hidden className="carousel__canvas" ref={canvas} />
          </div>
          
          {/* Placeholder image shown while WebGL content loads */}
          {showPlaceholder && placeholder && (
            <img
              aria-hidden // Hidden from screen readers (main content is described above)
              className={classNames('carousel__placeholder', {
                'carousel__placeholder--loaded': !prerender && loaded && textures, // Fade out when loaded
              })}
              src={placeholder}
              ref={placeholderRef}
              alt=""
              role="presentation" // Decorative image
            />
          )}
        </div>
        
        {/* Navigation button (previous) */}
        <button
          className="carousel__button carousel__button--prev"
          aria-label="Previous slide" // Accessible label for screen readers
          onClick={() => navigate({ direction: -1 })} // Go to previous image
          onMouseUp={blurOnMouseUp} // Remove focus after click for better UX
        >
          <ArrowLeft />
        </button>
        
        {/* Navigation button (next) */}
        <button
          className="carousel__button carousel__button--next"
          aria-label="Next slide" // Accessible label for screen readers
          onClick={() => navigate({ direction: 1 })} // Go to next image
          onMouseUp={blurOnMouseUp} // Remove focus after click for better UX
        >
          <ArrowRight />
        </button>
      </div>
      
      {/* Navigation dots/indicators for jumping to specific slides */}
      <div className="carousel__nav">
        {images.map((image, index) => (
          <button
            className="carousel__nav-button"
            key={image.alt} // Use alt text as unique key
            onClick={() => onNavClick(index)} // Jump directly to selected image
            onMouseUp={blurOnMouseUp} // Remove focus after click
            aria-label={`Jump to slide ${index + 1}`} // Accessible label with slide number
            aria-pressed={index === imageIndex} // Indicate current slide for screen readers
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
